// Retour d'expérience d'un ouvrage type (31/10/2026) : chantiers TERMINÉS où l'ouvrage a été posé, temps prévu par unité contre
// temps réel par unité — seulement quand le temps déclaré se rattache à cet ouvrage (jamais réparti). « Proposer ce temps »
// PRÉ-REMPLIT le réglage de l'ouvrage : rien n'est enregistré tant que la personne qui règle le catalogue n'a pas validé.
// Réservé à ceux qui encadrent les chantiers (le serveur refuse les autres) ; aucun nom de personne, aucun montant.
import { useQuery } from '@tanstack/react-query';
import { FlaskConical, History, Wand2 } from 'lucide-react';
import { api } from '../../lib/api.ts';
import { date, nombre, pluriel } from '../../lib/format.ts';
import { useSession } from '../../lib/session.tsx';
import { Alerte, Badge, Bouton, Chargement, EtatErreur } from '../../components/ui.tsx';
import '../../styles/chantiers.css';

interface Synthese { chantiers: number; quantite: string; prevues: string; reel: string; prevu_par_unite: string; reel_par_unite: string; rapport: string; ecart_pct: string }
export interface PropositionTemps {
  d_apres: 'reel' | 'demo'; chantiers: number; chantier_ids: string[]; rapport: string; par_unite_actuel: string | null; par_unite_propose: string | null;
  prevu_different_actuel: boolean; inchange: boolean;
  composants: { index: number; label: string; unit: string; avant: string | null; propose: string | null }[];
}
interface Retour {
  ouvrage: { id: string; code: string; label: string; unit: string };
  chantiers: { id: string; reference: string | null; title: string; demo: boolean; reception: string | null; retenu: boolean; motif: string | null;
    quantite: string | null; prevues: string | null; reel: string | null; prevu_par_unite: string | null; reel_par_unite: string | null; ecart_pct: string | null }[];
  synthese: { reel: Synthese | null; demo: Synthese | null };
  proposition: PropositionTemps | null;
  en_cours: number;
  droits: { proposer: boolean };
}

const h = (x: string | null) => (x === null ? '—' : `${nombre(x, 2)} h`);
const pct = (x: string) => `${Number(x) > 0 ? '+ ' : Number(x) < 0 ? '− ' : ''}${nombre(x.replace('-', ''), 1)} %`;

function LigneSynthese({ s, unite, demo }: { s: Synthese; unite: string; demo?: boolean }) {
  return (
    <p className="petit" style={{ margin: 0 }}>
      {demo && <Badge ton="ambre" icone={<FlaskConical size={14} aria-hidden />}>Démo</Badge>}{' '}
      D’après <strong>{pluriel(s.chantiers, 'chantier terminé', 'chantiers terminés')}</strong> ({nombre(s.quantite, 4)} {unite}) :
      prévu <strong className="chiffre">{h(s.prevu_par_unite)} / {unite}</strong>, réel <strong className="chiffre">{h(s.reel_par_unite)} / {unite}</strong>
      {' '}<span className="chiffre">({pct(s.ecart_pct)})</span>.
    </p>
  );
}

/** Panneau du retour d'expérience ; `proposer` (si fourni) reçoit la proposition pour pré-remplir le formulaire de réglage. */
export function RetourExperience({ ouvrageId, proposer }: { ouvrageId: string; proposer?: (p: PropositionTemps) => void }) {
  const { peut } = useSession();
  const permis = peut('chantier.affecter') || peut('chantier.corriger');
  const q = useQuery({ queryKey: ['ouvrage-retour', ouvrageId], queryFn: () => api.get<Retour>(`/api/ouvrages/${ouvrageId}/retour-experience`), enabled: permis });
  if (!permis) return null;
  if (q.isLoading) return <Chargement lignes={2} libelle="Retour d’expérience des chantiers…" />;
  if (q.isError) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const r = q.data!;
  const u = r.ouvrage.unit;
  const p = r.proposition;
  return (
    <section className="rex" aria-label="Retour d’expérience des chantiers">
      <h3 className="ouvrages-sous-titre"><History size={18} aria-hidden style={{ verticalAlign: '-3px' }} /> Retour d’expérience des chantiers</h3>
      {!r.chantiers.length ? (
        <p className="petit secondaire" style={{ margin: 0 }}>
          Aucun chantier terminé (réception déclarée) avec cet ouvrage{r.en_cours ? ` — ${pluriel(r.en_cours, 'chantier en cours', 'chantiers en cours')}` : ''}.
        </p>
      ) : (
        <>
          {r.synthese.reel && <LigneSynthese s={r.synthese.reel} unite={u} />}
          {r.synthese.demo && <LigneSynthese s={r.synthese.demo} unite={u} demo />}
          {!r.synthese.reel && !r.synthese.demo && <p className="petit secondaire" style={{ margin: 0 }}>Aucun chantier terminé n’a un temps déclaré rattachable à cet ouvrage.</p>}
          <ul className="rex-liste">
            {r.chantiers.map((c) => (
              <li key={c.id} className={c.retenu ? undefined : 'rex-exclu'}>
                <span className="petit"><span className="mono">{c.reference ?? '—'}</span> {c.title} {c.demo && <Badge ton="ambre" icone={false}>Démo</Badge>}</span>
                <span className="tres-petit discret">{c.reception ? `Réception du ${date(c.reception)}` : 'Chantier terminé'}</span>
                {c.retenu
                  ? <span className="petit chiffre">{nombre(c.quantite!, 4)} {u} — prévu {h(c.prevues)}, réel {h(c.reel)} — {h(c.prevu_par_unite)} contre {h(c.reel_par_unite)} / {u} ({pct(c.ecart_pct!)})</span>
                  : <span className="petit secondaire">Non retenu : {c.motif}</span>}
              </li>
            ))}
          </ul>
        </>
      )}
      {p && (
        <div className="pile" style={{ gap: 'var(--e-2)' }}>
          <p className="petit" style={{ margin: 0 }}>
            Proposition : chaque temps de l’ouvrage × <strong className="chiffre">{nombre(p.rapport, 4)}</strong> (réel ÷ prévu{p.d_apres === 'demo' ? ', chantiers de démonstration' : ''}),
            soit <strong className="chiffre">{h(p.par_unite_propose)} / {u}</strong> au lieu de {h(p.par_unite_actuel)} / {u}.
          </p>
          {p.prevu_different_actuel && (
            <Alerte ton="ambre">Le temps prévu sur ces chantiers diffère du temps actuel de l’ouvrage (réglé depuis, part fixe ou « penser à tout »
              ajoutés au devis) : la proposition s’applique au temps actuel.</Alerte>
          )}
          <ul className="petit chiffre rex-composants">
            {p.composants.filter((c) => c.avant !== c.propose).map((c) => (
              <li key={c.index}>{c.label} : {h(c.avant)} → <strong>{h(c.propose)}</strong> / {c.unit}</li>
            ))}
          </ul>
          {proposer && r.droits.proposer && !p.inchange && (
            <div><Bouton icone={<Wand2 aria-hidden />} onClick={() => proposer(p)}>Proposer ce temps</Bouton></div>
          )}
          {!r.droits.proposer && !p.inchange && <p className="tres-petit discret" style={{ margin: 0 }}>Le réglage du catalogue est réservé au chargé d’affaires et à l’administrateur.</p>}
        </div>
      )}
    </section>
  );
}
